/**
 * AdminShopSubmissions — `/admin/shop-submissions` — admin reviews
 * customer-submitted shops from the Refer page. Approve creates an
 * INACTIVE restaurants doc with the submitter stamped as the referrer;
 * reject records the reason and closes the submission.
 */
import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Check, X, Store, User, Phone, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = [
    { key: "PENDING",  label: "Pending"  },
    { key: "APPROVED", label: "Approved" },
    { key: "REJECTED", label: "Rejected" },
];

export default function AdminShopSubmissions() {
    const [tab, setTab] = useState("PENDING");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/shop-submissions", { params: { status: tab } });
            setItems(data?.items || []);
            setCounts(data?.counts || counts);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load");
        } finally { setBusy(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

    const approve = async (sid) => {
        if (!window.confirm("Approve this submission? An INACTIVE shop will be created; you can assign the owner + activate it from Shops next.")) return;
        try {
            const { data } = await api.post(`/admin/shop-submissions/${sid}/approve`);
            toast.success("Approved — shop created (INACTIVE).");
            if (data?.restaurant_id) {
                setTimeout(() => { window.location.href = `/admin/restaurants?highlight=${data.restaurant_id}`; }, 400);
            }
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    };

    const reject = async (sid) => {
        const reason = window.prompt("Reason for rejection (optional):", "") || "";
        try {
            await api.post(`/admin/shop-submissions/${sid}/reject`, { admin_note: reason });
            toast.success("Rejected.");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    };

    const currentCount = useMemo(() => counts[tab] || 0, [counts, tab]);

    return (
        <div data-testid="admin-shop-submissions">
            <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                <ClipboardList size={20} className="text-indigo-500" /> Shop Submissions
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
                Customer-submitted shop referrals from the Refer page. Approve to create an inactive shop
                stamped with the submitter as the referrer, then assign the owner in Shops.
            </p>

            <div className="mt-6 flex gap-2 border-b border-border">
                {STATUSES.map((s) => (
                    <button
                        key={s.key}
                        data-testid={`sub-tab-${s.key}`}
                        onClick={() => setTab(s.key)}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
                            tab === s.key ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        {s.label} {counts[s.key] > 0 && <span className="ml-1 text-xs bg-muted rounded-full px-1.5 py-0.5">{counts[s.key]}</span>}
                    </button>
                ))}
            </div>

            <div className="mt-4">
                {busy && items.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-2xl">
                        No {tab.toLowerCase()} submissions.
                    </div>
                ) : (
                    <ul className="space-y-3">
                        {items.map((s) => (
                            <li key={s.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`sub-row-${s.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold inline-flex items-center gap-2">
                                            <Store size={14} className="text-primary" /> {s.shop_name}
                                        </p>
                                        <div className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-muted-foreground">
                                            <span className="inline-flex items-center gap-1.5"><User size={12} /> Owner: <span className="text-foreground">{s.owner_name}</span></span>
                                            <span className="inline-flex items-center gap-1.5"><Phone size={12} /> {s.owner_mobile}</span>
                                            <span className="inline-flex items-center gap-1.5 sm:col-span-2"><MapPin size={12} /> {s.address_line}, {s.city}</span>
                                            {s.business_category_name && (
                                                <span className="inline-flex items-center gap-1.5 sm:col-span-2"><Store size={12} /> Category: <span className="text-foreground">{s.business_category_name}</span></span>
                                            )}
                                        </div>
                                        {s.notes && (
                                            <p className="mt-2 text-xs bg-muted/60 rounded-lg px-2 py-1.5">{s.notes}</p>
                                        )}
                                        <p className="mt-2 text-[11px] text-muted-foreground">
                                            Referred by <b>{s.referrer_name || s.referrer_mobile || "—"}</b>
                                            {s.referrer_mobile && s.referrer_name && s.referrer_mobile !== s.referrer_name ? ` (${s.referrer_mobile})` : ""}
                                            {" · "}{new Date(s.created_at).toLocaleString()}
                                        </p>
                                        {s.admin_note && (
                                            <p className="mt-1 text-[11px] text-amber-700">Admin note: {s.admin_note}</p>
                                        )}
                                    </div>
                                    {tab === "PENDING" && (
                                        <div className="shrink-0 flex flex-col gap-1.5">
                                            <button
                                                data-testid={`sub-approve-${s.id}`}
                                                onClick={() => approve(s.id)}
                                                className="text-xs font-bold rounded-lg bg-emerald-600 text-white px-3 py-1.5 inline-flex items-center gap-1"
                                            >
                                                <Check size={12} /> Approve
                                            </button>
                                            <button
                                                data-testid={`sub-reject-${s.id}`}
                                                onClick={() => reject(s.id)}
                                                className="text-xs font-bold rounded-lg bg-red-100 text-red-700 border border-red-200 px-3 py-1.5 inline-flex items-center gap-1"
                                            >
                                                <X size={12} /> Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {tab === "PENDING" && currentCount === 0 && (
                <p className="mt-6 text-xs text-muted-foreground">
                    Tip: this list is powered by the customer <code className="bg-muted px-1 rounded">/refer</code> page.
                    You can toggle the customer "Add Shop" card on/off in <b>Admin → Shop Referral Programme</b>.
                </p>
            )}
        </div>
    );
}
